import React, { useState, useEffect, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  styled,
  alpha,
  TextField,
  InputAdornment,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ToggleButtonGroup,
  ToggleButton,
  Fade,
  Stack,
  Button,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SearchIcon from '@mui/icons-material/Search';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewTimelineIcon from '@mui/icons-material/ViewTimeline';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import DescriptionIcon from '@mui/icons-material/Description';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import { SEOHead } from '../components/SEOHead';
import { cyber } from '../theme';

// --- Types ---
interface AnalyticsReport {
  slug: string;
  title: string;
  date: string;
  severity: string;
  classification: string;
  description: string;
  tags: string[];
  sources_count: number;
  author: string;
}

// --- Constants ---
const severityColors: Record<string, { bg: string; color: string; border: string }> = {
  CRITICAL: { bg: 'rgba(244, 67, 54, 0.15)', color: '#ff5252', border: 'rgba(244, 67, 54, 0.4)' },
  HIGH: { bg: 'rgba(255, 152, 0, 0.15)', color: '#ffab40', border: 'rgba(255, 152, 0, 0.4)' },
  MEDIUM: { bg: 'rgba(255, 235, 59, 0.15)', color: '#ffee58', border: 'rgba(255, 235, 59, 0.4)' },
  LOW: { bg: 'rgba(76, 175, 80, 0.15)', color: '#69f0ae', border: 'rgba(76, 175, 80, 0.4)' },
  INFORMATIONAL: { bg: 'rgba(33, 150, 243, 0.15)', color: '#448aff', border: 'rgba(33, 150, 243, 0.4)' },
};

const SEVERITY_ORDER: Record<string, number> = {
  CRITICAL: 0,
  HIGH: 1,
  MEDIUM: 2,
  LOW: 3,
  INFORMATIONAL: 4,
};

// --- Styled Components ---
const HeroSection = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${cyber.bgPaper} 0%, #3a2d6e 55%, ${cyber.primary} 130%)`,
  border: `1px solid ${alpha(cyber.primary, 0.35)}`,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(4),
  marginBottom: theme.spacing(4),
  color: 'white',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    right: '-10%',
    width: '300px',
    height: '300px',
    background: alpha(cyber.primaryLight, 0.25),
    borderRadius: '50%',
    filter: 'blur(80px)',
  },
}));

const StatsBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  marginTop: theme.spacing(3),
  flexWrap: 'wrap',
}));

const StatItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  background: 'rgba(255, 255, 255, 0.15)',
  borderRadius: theme.spacing(1),
  backdropFilter: 'blur(10px)',
}));

const StatsContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(3),
  marginBottom: theme.spacing(4),
  gridTemplateColumns: 'repeat(1, 1fr)',
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(4, 1fr)',
  },
}));

const GradientCard = styled(Card)(() => ({
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: cyber.glow,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: cyber.gradient,
  },
}));

const SearchPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  background: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
}));

const ReportCard = styled(Card)(({ theme }) => ({
  height: '100%',
  background: alpha(theme.palette.background.paper, 0.7),
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'all 0.3s ease-in-out',
  position: 'relative',
  overflow: 'visible',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: cyber.glow,
    borderColor: alpha(theme.palette.primary.main, 0.3),
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: cyber.gradient,
    opacity: 0,
    transition: 'opacity 0.3s ease-in-out',
    borderRadius: '12px 12px 0 0',
  },
  '&:hover::before': {
    opacity: 1,
  },
}));

const SeverityBadge = styled(Box)<{ severity: string }>(({ severity }) => {
  const colors = severityColors[severity] || severityColors.INFORMATIONAL;
  return {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: '12px',
    fontSize: '0.7rem',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    background: colors.bg,
    color: colors.color,
    border: `1px solid ${colors.border}`,
  };
});

// --- Component ---
const AnalyticsPage: React.FC = () => {
  const [reports, setReports] = useState<AnalyticsReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter/search state
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);
  const [severityFilter, setSeverityFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('date_desc');
  const [viewMode, setViewMode] = useState<'grid' | 'timeline'>('grid');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  useEffect(() => {
    fetch('/analytics/index.json')
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data: AnalyticsReport[]) => {
        data.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        setReports(data);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  // --- Computed values ---
  const stats = useMemo(() => {
    const totalReports = reports.length;
    const totalSources = reports.reduce((sum, r) => sum + r.sources_count, 0);
    const criticalHighCount = reports.filter(
      (r) => r.severity === 'CRITICAL' || r.severity === 'HIGH'
    ).length;
    const uniqueTags = new Set(reports.flatMap((r) => r.tags)).size;
    return { totalReports, totalSources, criticalHighCount, uniqueTags };
  }, [reports]);

  const tagCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    reports.forEach((r) => r.tags.forEach((t) => { counts[t] = (counts[t] || 0) + 1; }));
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 15);
  }, [reports]);

  const filteredReports = useMemo(() => {
    let result = [...reports];

    // Search
    if (debouncedSearchTerm) {
      const lower = debouncedSearchTerm.toLowerCase();
      result = result.filter(
        (r) =>
          r.title.toLowerCase().includes(lower) ||
          r.description.toLowerCase().includes(lower) ||
          r.tags.some((t) => t.toLowerCase().includes(lower))
      );
    }

    // Severity filter
    if (severityFilter) {
      result = result.filter((r) => r.severity === severityFilter);
    }

    // Tags filter
    if (selectedTags.length > 0) {
      result = result.filter((r) => selectedTags.every((t) => r.tags.includes(t)));
    }

    // Sort
    if (sortBy === 'date_desc') {
      result.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    } else if (sortBy === 'date_asc') {
      result.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    } else if (sortBy === 'severity') {
      result.sort(
        (a, b) => (SEVERITY_ORDER[a.severity] ?? 99) - (SEVERITY_ORDER[b.severity] ?? 99)
      );
    }

    return result;
  }, [reports, debouncedSearchTerm, severityFilter, selectedTags, sortBy]);

  // --- Grouped by month (for timeline) ---
  const groupedByMonth = useMemo(() => {
    const groups: { label: string; reports: AnalyticsReport[] }[] = [];
    const map = new Map<string, AnalyticsReport[]>();
    filteredReports.forEach((r) => {
      const d = new Date(r.date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(r);
    });
    map.forEach((reps, key) => {
      const [year, month] = key.split('-');
      const label = new Date(Number(year), Number(month) - 1).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
      });
      groups.push({ label, reports: reps });
    });
    return groups;
  }, [filteredReports]);

  const hasActiveFilters = debouncedSearchTerm || severityFilter || selectedTags.length > 0;

  const clearFilters = () => {
    setSearchTerm('');
    setSeverityFilter('');
    setSelectedTags([]);
  };

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatShortDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // --- Render helpers ---
  const renderStatCard = (
    icon: React.ReactNode,
    value: string | number,
    label: string,
    color: string
  ) => (
    <GradientCard>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: alpha(color, 0.15),
            color: color,
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ fontFamily: cyber.fontMono }}>
            {value}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
        </Box>
      </CardContent>
    </GradientCard>
  );

  const renderReportCardContent = (report: AnalyticsReport) => (
    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
      {/* Badges row */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <SeverityBadge severity={report.severity}>{report.severity}</SeverityBadge>
        <Chip
          label={report.classification}
          size="small"
          variant="outlined"
          sx={{ fontSize: '0.7rem', height: 22 }}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ ml: 'auto', fontFamily: cyber.fontMono, fontSize: '0.7rem' }}
        >
          {formatDate(report.date)}
        </Typography>
      </Box>

      {/* Title */}
      <Typography variant="h6" component="h2" sx={{ mb: 1.5, fontWeight: 600, lineHeight: 1.3 }}>
        {report.title}
      </Typography>

      {/* Description */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
        {report.description}
      </Typography>

      {/* Tags */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
        {report.tags.slice(0, 5).map((tag) => (
          <Chip
            key={tag}
            label={tag}
            size="small"
            sx={{
              fontSize: '0.7rem',
              height: 20,
              bgcolor: alpha(cyber.primary, 0.15),
              color: cyber.primaryLight,
              border: '1px solid',
              borderColor: alpha(cyber.primary, 0.4),
            }}
          />
        ))}
      </Box>

      {/* Footer */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
        {report.sources_count > 0 && (
          <Typography
            variant="caption"
            sx={{ color: alpha(cyber.accent.cyan, 0.9), fontFamily: cyber.fontMono, fontSize: '0.7rem' }}
          >
            {report.sources_count} sources analyzed
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'primary.main', ml: 'auto' }}>
          <Typography variant="caption" fontWeight={600}>
            Read Report
          </Typography>
          <ArrowForwardIcon sx={{ fontSize: 14 }} />
        </Box>
      </Box>
    </CardContent>
  );

  return (
    <>
      <SEOHead
        title="Agentic Reports"
        description="In-depth threat intelligence reports autonomously generated by AI agents analyzing data collected by TI Mindmap HUB via MCP."
        canonical="/analytics"
        type="website"
        tags={['threat intelligence', 'agentic reports', 'AI threat analysis', 'cyber threat report', 'CTI', 'MCP']}
      />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* ===== HERO SECTION ===== */}
        <HeroSection>
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <SmartToyIcon sx={{ fontSize: 32 }} />
              <Typography variant="h4" component="h1" fontWeight="bold">
                Agentic Reports
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ opacity: 0.9, maxWidth: 700 }}>
              In-depth threat intelligence reports autonomously generated by AI agents analyzing
              data collected by TI Mindmap HUB via MCP.
            </Typography>

            {!loading && reports.length > 0 && (
              <StatsBox>
                <StatItem>
                  <DescriptionIcon sx={{ fontSize: 18, color: cyber.accent.coral }} />
                  <Typography variant="body2" fontWeight={600}>
                    {stats.totalReports} Report{stats.totalReports !== 1 ? 's' : ''}
                  </Typography>
                </StatItem>
                <StatItem>
                  <RssFeedIcon sx={{ fontSize: 18, color: cyber.accent.cyan }} />
                  <Typography variant="body2" fontWeight={600}>
                    {stats.totalSources} Sources Analyzed
                  </Typography>
                </StatItem>
                {stats.criticalHighCount > 0 && (
                  <StatItem>
                    <WarningAmberIcon sx={{ fontSize: 18, color: cyber.accent.amber }} />
                    <Typography variant="body2" fontWeight={600}>
                      {stats.criticalHighCount} Critical/High
                    </Typography>
                  </StatItem>
                )}
                <StatItem>
                  <LocalOfferIcon sx={{ fontSize: 18, color: cyber.accent.green }} />
                  <Typography variant="body2" fontWeight={600}>
                    {stats.uniqueTags} Tags
                  </Typography>
                </StatItem>
              </StatsBox>
            )}
          </Box>
        </HeroSection>

        {/* ===== STAT CARDS ===== */}
        {!loading && reports.length > 0 && (
          <StatsContainer>
            {renderStatCard(<DescriptionIcon />, stats.totalReports, 'Total Reports', cyber.primary)}
            {renderStatCard(
              <WarningAmberIcon />,
              stats.criticalHighCount,
              'Critical / High',
              '#ff5252'
            )}
            {renderStatCard(<RssFeedIcon />, stats.totalSources, 'Sources Analyzed', cyber.accent.cyan)}
            {renderStatCard(<LocalOfferIcon />, stats.uniqueTags, 'Unique Tags', cyber.accent.green)}
          </StatsContainer>
        )}

        {/* ===== SEARCH & FILTERS ===== */}
        {!loading && reports.length > 0 && (
          <SearchPaper>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ md: 'center' }}
            >
              {/* Search */}
              <TextField
                fullWidth
                size="small"
                placeholder="Search reports by title, description, or tag..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ flex: 2 }}
              />

              {/* Severity filter */}
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Severity</InputLabel>
                <Select
                  value={severityFilter}
                  label="Severity"
                  onChange={(e) => setSeverityFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {Object.keys(severityColors).map((s) => (
                    <MenuItem key={s} value={s}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: severityColors[s].color,
                          }}
                        />
                        {s}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Sort */}
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Sort by</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort by"
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="date_desc">Newest first</MenuItem>
                  <MenuItem value="date_asc">Oldest first</MenuItem>
                  <MenuItem value="severity">Severity</MenuItem>
                </Select>
              </FormControl>

              {/* View toggle */}
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, v) => v && setViewMode(v)}
                size="small"
              >
                <ToggleButton value="grid" aria-label="grid view">
                  <GridViewIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="timeline" aria-label="timeline view">
                  <ViewTimelineIcon fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </SearchPaper>
        )}

        {/* ===== POPULAR TAGS ===== */}
        {!loading && tagCounts.length > 0 && (
          <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1, fontWeight: 600 }}>
              POPULAR TAGS
            </Typography>
            {tagCounts.map(([tag, count]) => {
              const isActive = selectedTags.includes(tag);
              return (
                <Chip
                  key={tag}
                  label={`${tag} (${count})`}
                  size="small"
                  onClick={() => toggleTag(tag)}
                  sx={{
                    fontSize: '0.7rem',
                    height: 24,
                    cursor: 'pointer',
                    bgcolor: isActive ? alpha(cyber.primary, 0.3) : alpha(cyber.primary, 0.08),
                    color: cyber.primaryLight,
                    border: '1px solid',
                    borderColor: isActive ? alpha(cyber.primary, 0.5) : alpha(cyber.primary, 0.15),
                    fontWeight: isActive ? 700 : 400,
                    '&:hover': {
                      bgcolor: alpha(cyber.primary, 0.2),
                    },
                  }}
                />
              );
            })}
            {selectedTags.length > 0 && (
              <Chip
                label="Clear tags"
                size="small"
                onDelete={() => setSelectedTags([])}
                sx={{ fontSize: '0.7rem', height: 24 }}
              />
            )}
          </Box>
        )}

        {/* ===== RESULTS COUNT ===== */}
        {!loading && reports.length > 0 && (
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Showing {filteredReports.length} of {reports.length} report
              {reports.length !== 1 ? 's' : ''}
            </Typography>
            {hasActiveFilters && (
              <Button
                size="small"
                startIcon={<FilterListOffIcon />}
                onClick={clearFilters}
                sx={{ textTransform: 'none' }}
              >
                Clear all filters
              </Button>
            )}
          </Box>
        )}

        {/* ===== LOADING ===== */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {/* ===== ERROR ===== */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load reports: {error}
          </Alert>
        )}

        {/* ===== EMPTY STATE (no reports at all) ===== */}
        {!loading && !error && reports.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <SmartToyIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No analytics reports published yet.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Check back soon for in-depth threat intelligence analyses.
            </Typography>
          </Box>
        )}

        {/* ===== FILTERED EMPTY STATE ===== */}
        {!loading && !error && reports.length > 0 && filteredReports.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <SearchIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No reports match your filters.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              Try adjusting your search or filters to find what you're looking for.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<FilterListOffIcon />}
              onClick={clearFilters}
              sx={{ textTransform: 'none' }}
            >
              Clear all filters
            </Button>
          </Box>
        )}

        {/* ===== GRID VIEW ===== */}
        {!loading && filteredReports.length > 0 && viewMode === 'grid' && (
          <Grid container spacing={3}>
            {filteredReports.map((report, index) => (
              <Grid size={{ xs: 12, md: 6, lg: 4 }} key={report.slug}>
                <Fade in timeout={300 + (index % 12) * 80}>
                  <ReportCard>
                    <CardActionArea
                      component={RouterLink}
                      to={`/analytics/${report.slug}`}
                      sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                    >
                      {renderReportCardContent(report)}
                    </CardActionArea>
                  </ReportCard>
                </Fade>
              </Grid>
            ))}
          </Grid>
        )}

        {/* ===== TIMELINE VIEW ===== */}
        {!loading && filteredReports.length > 0 && viewMode === 'timeline' && (
          <Box sx={{ position: 'relative' }}>
            {groupedByMonth.map((group, groupIdx) => (
              <Box key={group.label} sx={{ mb: 4 }}>
                {/* Month header */}
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: cyber.fontMono,
                    color: alpha(cyber.accent.cyan, 0.85),
                    mb: 3,
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    letterSpacing: '0.5px',
                  }}
                >
                  {String(groupIdx + 1).padStart(2, '0')}{' // '}
                  <Box component="span" sx={{ color: '#fff' }}>
                    {group.label}
                  </Box>
                </Typography>

                {/* Timeline entries */}
                <Box sx={{ position: 'relative', pl: { xs: 4, md: 6 } }}>
                  {/* Vertical line */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: { xs: 7, md: 11 },
                      top: 0,
                      bottom: 0,
                      width: 2,
                      bgcolor: alpha(cyber.primary, 0.25),
                    }}
                  />

                  {group.reports.map((report, index) => {
                    const dotColor = severityColors[report.severity]?.color || '#448aff';
                    return (
                      <Fade in timeout={300 + index * 100} key={report.slug}>
                        <Box sx={{ position: 'relative', mb: 3 }}>
                          {/* Severity dot */}
                          <Box
                            sx={{
                              position: 'absolute',
                              left: { xs: -28, md: -38 },
                              top: 20,
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              bgcolor: dotColor,
                              border: '3px solid',
                              borderColor: 'background.default',
                              boxShadow: `0 0 12px ${alpha(dotColor, 0.6)}`,
                              zIndex: 1,
                            }}
                          />

                          {/* Date chip (mobile: inline, desktop: absolute left) */}
                          <Typography
                            variant="caption"
                            sx={{
                              display: { xs: 'block', md: 'none' },
                              color: 'text.secondary',
                              mb: 0.5,
                              fontWeight: 600,
                              fontSize: '0.75rem',
                            }}
                          >
                            {formatShortDate(report.date)}
                          </Typography>

                          {/* Report card */}
                          <ReportCard>
                            <CardActionArea
                              component={RouterLink}
                              to={`/analytics/${report.slug}`}
                              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                            >
                              {renderReportCardContent(report)}
                            </CardActionArea>
                          </ReportCard>
                        </Box>
                      </Fade>
                    );
                  })}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Container>
    </>
  );
};

export default AnalyticsPage;
